import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { useSketchStore } from '../../state/useSketchStore';
import { useAppStore } from '../../state/store';
import { SKETCH_COLORS } from '../../themes/colors';
import { frameSectionPlane } from '../../core/sketch/sectionPlane';
import { gumballOwnsPointer } from '../../state/useGumballStore';

/**
 * Visual section-plane marker (Sketch language): a translucent quad lying
 * exactly ON the active section plane, a border outline and a short arrow
 * along the plane normal. The skin quad is the section-plane ENTITY's pick
 * target in Select mode (click = select the plane, gumball move/rotate);
 * an active tool keeps its own pick semantics, and the border/arrow stay
 * raycast-transparent so they never swallow face picks. Geometry sits on
 * the clipped side's boundary so the global clipping never cuts the marker
 * away.
 */

interface MarkerPose {
    center: THREE.Vector3;
    quaternion: THREE.Quaternion;
    size: number;
    /** Line geometries derived from the size; disposed on swap/unmount. */
    borderGeometry: THREE.BufferGeometry;
    arrowGeometry: THREE.BufferGeometry;
}

export function SketchSectionPlaneMarker() {
    const sectionPlane = useSketchStore((s) => s.sectionPlane);
    const sectionPlaneSelected = useSketchStore((s) => s.sectionPlaneSelected);
    const solids = useSketchStore((s) => s.solids);

    const pose = useMemo<MarkerPose | null>(() => {
        if (!sectionPlane) return null;
        const frame = frameSectionPlane(sectionPlane, solids);

        const half = frame.size / 2;
        const borderGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(-half, -half, 0),
            new THREE.Vector3(half, -half, 0),
            new THREE.Vector3(half, half, 0),
            new THREE.Vector3(-half, half, 0),
        ]);
        const arrowLength = frame.size * 0.2;
        const arrowGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, 0, arrowLength * 0.75),
        ]);
        return { center: frame.center, quaternion: frame.quaternion, size: frame.size, borderGeometry, arrowGeometry };
    }, [sectionPlane, solids]);

    useEffect(() => {
        if (!pose) return;
        return () => {
            pose.borderGeometry.dispose();
            pose.arrowGeometry.dispose();
        };
    }, [pose]);

    if (!sectionPlane || !pose) return null;

    // Select-mode pick of the plane ENTITY through its skin. An active tool
    // (drawing, measuring, the Section Plane tool itself...) keeps its own
    // pick semantics, and a gizmo grab keeps the manipulator's pointer.
    // The plane selection is exclusive: solids and foreign references
    // deselect with it.
    const handleSkinDown = (event: ThreeEvent<PointerEvent>) => {
        if (event.button !== 0 || gumballOwnsPointer()) return;
        if (useAppStore.getState().activeTool !== 'select') return;
        if (!useSketchStore.getState().sectionPlane) return;
        event.stopPropagation();
        useSketchStore.getState().select([]);
        useAppStore.getState().select([]);
        useSketchStore.getState().setSectionPlaneSelected(true);
    };

    const arrowLength = pose.size * 0.2;
    const arrowQuaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 1));

    return (
        <group position={pose.center} quaternion={pose.quaternion}>
            {/* Translucent plane skin — unlit so it reads the same in every style */}
            <mesh onPointerDown={handleSkinDown} renderOrder={1}>
                <planeGeometry args={[pose.size, pose.size]} />
                <meshBasicMaterial
                    color={SKETCH_COLORS.sectionPlane}
                    transparent
                    opacity={sectionPlaneSelected ? 0.2 : 0.12}
                    side={THREE.DoubleSide}
                    depthWrite={false}
                />
            </mesh>
            <lineLoop geometry={pose.borderGeometry} raycast={() => null} renderOrder={2}>
                <lineBasicMaterial
                    color={SKETCH_COLORS.sectionPlane}
                    transparent
                    opacity={sectionPlaneSelected ? 1 : 0.9}
                    depthWrite={false}
                />
            </lineLoop>
            {/* Direction arrow: shaft along +Z of the plane frame, cone tip on top */}
            <lineSegments geometry={pose.arrowGeometry} raycast={() => null} renderOrder={2}>
                <lineBasicMaterial color={SKETCH_COLORS.sectionPlane} transparent opacity={0.9} depthWrite={false} />
            </lineSegments>
            <mesh
                position={[0, 0, arrowLength * 0.75 + pose.size * 0.03]}
                quaternion={arrowQuaternion}
                raycast={() => null}
                renderOrder={2}
            >
                <coneGeometry args={[pose.size * 0.03, pose.size * 0.06, 12]} />
                <meshBasicMaterial color={SKETCH_COLORS.sectionPlane} depthWrite={false} />
            </mesh>
        </group>
    );
}
